import { useMemo } from "react";
import { Smartphone } from "lucide-react";
import { SectionCard, SectionHeader, KpiChip, ChartCard, MiniPie, MiniBar, inr, last6Months, monthKey, SECTION_ACCENTS } from "./dashboardUi";

const LEAD_STATUS_LABEL = { open: "Open", accepted: "Accepted", on_hold: "On Hold", dropped: "Dropped", converted: "Converted" };
const C = SECTION_ACCENTS.customerReferral.chart;

export default function CustomerReferralSection({ leads, payouts, delay }) {
  const insights = useMemo(() => {
    const referred = leads.filter((l) => l.referred_by_customer_id);
    const selfRaised = leads.filter((l) => !l.referred_by_customer_id && l.source === "public_app");

    const statusCounts = {};
    const byMonth = {};
    for (const l of referred) {
      statusCounts[l.status] = (statusCounts[l.status] || 0) + 1;
      const k = monthKey(l.created_at);
      if (k) byMonth[k] = (byMonth[k] || 0) + 1;
    }

    const custPayouts = payouts.filter((p) => p.referrer_type === "customer");
    const payoutByStatus = { pending: 0, processing: 0, paid: 0 };
    for (const p of custPayouts) payoutByStatus[p.payment_status || "pending"] += Number(p.payment_amount || 0);

    return {
      total: referred.length,
      converted: statusCounts.converted || 0,
      open: statusCounts.open || 0,
      dropped: statusCounts.dropped || 0,
      selfRaised: selfRaised.length,
      payoutByStatus,
      statusData: Object.entries(statusCounts).map(([k, value]) => ({ name: LEAD_STATUS_LABEL[k] || k, value })),
      payoutPieData: Object.entries(payoutByStatus).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0),
      trendData: last6Months(byMonth),
    };
  }, [leads, payouts]);

  return (
    <SectionCard accent="customerReferral" delay={delay} testid="dashboard-customer-referral-section">
      <SectionHeader icon={Smartphone} accent="customerReferral" title="Customer App Referrals" subtitle="'Refer a Friend' leads sourced directly from the Customer App" />
      <div className="flex items-center gap-1 flex-wrap mb-3">
        <KpiChip label="Total Referrals" value={insights.total} tone="bg-[#0F766E] text-white" to="/crm/leads" />
        <KpiChip label="Converted" value={insights.converted} tone="bg-emerald-50 text-emerald-800" to="/crm/leads" />
        <KpiChip label="Open" value={insights.open} tone="bg-sky-50 text-sky-800" to="/crm/leads" />
        <KpiChip label="Dropped" value={insights.dropped} to="/crm/leads" />
        <KpiChip label="Self-raised via App" value={insights.selfRaised} to="/crm/leads" />
        <KpiChip label="Pending Payout" value={inr(insights.payoutByStatus.pending)} tone="bg-slate-100 text-slate-700" to="/crm/referrer-payouts" />
        <KpiChip label="Paid Payout" value={inr(insights.payoutByStatus.paid)} tone="bg-emerald-50 text-emerald-800" to="/crm/referrer-payouts" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-customer-referral-charts">
        <ChartCard title="Referral Lead Status" testid="chart-cust-ref-status"><MiniPie data={insights.statusData} colors={C} to="/crm/leads" /></ChartCard>
        <ChartCard title="Referral Payout Status (₹)" testid="chart-cust-ref-payout"><MiniPie data={insights.payoutPieData} colors={C} formatter={(v) => inr(v)} to="/crm/referrer-payouts" /></ChartCard>
        <ChartCard title="Referral Trend (6mo)" testid="chart-cust-ref-trend"><MiniBar data={insights.trendData} color={C[0]} to="/crm/leads" /></ChartCard>
      </div>
    </SectionCard>
  );
}
